import { styled } from '@mui/material/styles';
import { Box, Button, Typography } from '@mui/material';

// Honey color palette (matching homeCommunity)
const honeyColors = {
    primary: '#F1C40F',
    primaryLight: '#F4D03F',
    primaryDark: '#D4AC0D',
    amber: '#FFB74D',
    cream: '#FFF8E1',
    honeycomb: '#D4A574',
    textPrimary: '#3E2723',
    textSecondary: '#5D4037',
    gradientPrimary: 'linear-gradient(135deg, #F1C40F 0%, #FFB74D 100%)',
    shadowMedium: '0 4px 16px rgba(212, 165, 116, 0.25)',
    shadowHeavy: '0 8px 32px rgba(212, 165, 116, 0.35)',
};

export const BannerContainer = styled(Box)(({ theme }) => ({
    background: `linear-gradient(135deg, ${honeyColors.cream} 0%, #FFF5E1 100%)`,
    borderRadius: '20px',
    padding: '32px',
    marginBottom: '24px',
    border: `2px solid ${honeyColors.primaryLight}`,
    boxShadow: honeyColors.shadowMedium,
    position: 'relative',
    overflow: 'hidden',

    '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        right: '-10%',
        width: '300px',
        height: '300px',
        background: `radial-gradient(circle, ${honeyColors.primary}20 0%, transparent 70%)`,
        borderRadius: '50%',
    },

    [theme.breakpoints.down('md')]: {
        padding: '24px',
    },

    [theme.breakpoints.down('sm')]: {
        padding: '20px',
        borderRadius: '16px',
    },
}));

export const BannerContent = styled(Box)({
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
});

export const BannerHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '8px',

    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '12px',
    },
}));

export const WelcomeIcon = styled(Box)({
    width: '56px',
    height: '56px',
    borderRadius: '16px',
    background: honeyColors.gradientPrimary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: honeyColors.shadowMedium,

    '& svg': {
        fontSize: '28px',
        color: honeyColors.textPrimary,
    },
});

export const BannerTitle = styled(Typography)({
    fontWeight: 800,
    color: honeyColors.textPrimary,
    fontSize: '1.5rem',
    lineHeight: 1.3,
});

export const BannerSubtitle = styled(Typography)({
    color: honeyColors.textSecondary,
    fontSize: '1rem',
    lineHeight: 1.6,
    maxWidth: '600px',
});

export const BenefitsList = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginTop: '8px',

    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: 'repeat(2, 1fr)',
    },

    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        gap: '12px',
    },
}));

export const BenefitItem = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    background: 'rgba(255, 255, 255, 0.8)',
    borderRadius: '12px',
    border: `1px solid ${honeyColors.primaryLight}40`,
    transition: 'all 0.2s ease',

    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(212, 165, 116, 0.2)',
    },

    '& .benefit-icon': {
        width: '36px',
        height: '36px',
        borderRadius: '10px',
        background: `${honeyColors.primary}20`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,

        '& svg': {
            fontSize: '18px',
            color: honeyColors.primaryDark,
        },
    },

    '& .benefit-text': {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },

    '& .benefit-label': {
        fontWeight: 600,
        fontSize: '0.9rem',
        color: honeyColors.textPrimary,
    },

    '& .benefit-value': {
        fontSize: '0.8rem',
        color: honeyColors.textSecondary,
    },
});

export const JoinButton = styled(Button)({
    background: honeyColors.gradientPrimary,
    color: honeyColors.textPrimary,
    fontWeight: 700,
    fontSize: '1rem',
    padding: '14px 32px',
    borderRadius: '14px',
    textTransform: 'none',
    boxShadow: honeyColors.shadowMedium,
    alignSelf: 'flex-start',
    marginTop: '8px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

    '&:hover': {
        background: `linear-gradient(135deg, ${honeyColors.primaryDark} 0%, ${honeyColors.primary} 100%)`,
        transform: 'translateY(-2px)',
        boxShadow: honeyColors.shadowHeavy,
    },
});

export const FreeLabel = styled(Typography)({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.85rem',
    color: '#4CAF50',
    fontWeight: 600,
    marginTop: '4px',

    '& svg': {
        fontSize: '16px',
    },
});
